import React, { useState, useEffect, useRef } from 'react';
import { 
  Receipt, Plus, Search, Download, Trash2, Edit, Eye, 
  Calendar, User, Building, Loader2, FileSpreadsheet, 
  ChevronDown, TrendingUp, PiggyBank, X, Upload, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { gastosAPI, contactosAPI, usuariosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Tipos de impuesto
const TIPOS_IMPUESTO = [
  { value: 'IVA 21%', label: 'IVA 21%', iva: 21 },
  { value: 'IVA 10%', label: 'IVA 10%', iva: 10 },
  { value: 'IVA 4%', label: 'IVA 4%', iva: 4 },
  { value: 'IVA 0%', label: 'Exento IVA', iva: 0 },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2 
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

const ControlesGastos = () => {
  const [gastos, setGastos] = useState([]);
  const [tiposGasto, setTiposGasto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filtros de exportación
  const [exportFechaDesde, setExportFechaDesde] = useState('');
  const [exportFechaHasta, setExportFechaHasta] = useState('');
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total_controles: 0,
    total_importes: 0,
    total_iva: 0,
    total_irpf: 0,
    total_coste: 0
  });
  
  // Contactos y usuarios para selects
  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [facturadores, setFacturadores] = useState([]);
  
  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  
  // Form
  const [form, setForm] = useState({
    tipo_gasto_id: '',
    tipo_gasto_nombre: '',
    proveedor_id: '',
    proveedor_nombre: '',
    cliente_id: '',
    cliente_nombre: '',
    facturador_id: '',
    facturador_nombre: '',
    fecha: new Date().toISOString().split('T')[0],
    descripcion: '',
    importe_base: 0,
    tipo_impuesto: 'IVA 21%',
    porcentaje_iva: 21,
    porcentaje_retencion: 0
  });

  useEffect(() => {
    loadData();
  }, [currentPage, itemsPerPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar gastos con paginación
      const [gastosRes, tiposRes, statsRes, countRes] = await Promise.all([
        gastosAPI.getAll({ 
          skip: (currentPage - 1) * itemsPerPage, 
          limit: itemsPerPage,
          search: search || undefined
        }),
        gastosAPI.getTipos(),
        gastosAPI.getStats({}),
        gastosAPI.getCount({ search: search || undefined })
      ]);
      
      setGastos(gastosRes.data);
      setTiposGasto(tiposRes.data);
      setStats(statsRes.data);
      setTotalCount(countRes.data.count);
      
      // Cargar contactos
      try {
        const contactosRes = await contactosAPI.getAll();
        const allContactos = contactosRes.data;
        setProveedores(allContactos.filter(c => c.tipo === 'proveedor'));
        setClientes(allContactos.filter(c => c.tipo === 'cliente'));
      } catch (err) {
        console.log('No se pudieron cargar contactos');
      }
      
      // Cargar usuarios como facturadores
      try {
        const usersRes = await usuariosAPI.getAll();
        setFacturadores(usersRes.data);
      } catch (err) {
        console.log('No se pudieron cargar usuarios');
      }
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    loadData();
  };

  const resetForm = () => {
    setForm({
      tipo_gasto_id: '',
      tipo_gasto_nombre: '',
      proveedor_id: '',
      proveedor_nombre: '',
      cliente_id: '',
      cliente_nombre: '',
      facturador_id: '',
      facturador_nombre: '',
      fecha: new Date().toISOString().split('T')[0],
      descripcion: '',
      importe_base: 0,
      tipo_impuesto: 'IVA 21%',
      porcentaje_iva: 21,
      porcentaje_retencion: 0
    });
    setEditingGasto(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (gasto) => {
    setForm({
      tipo_gasto_id: gasto.tipo_gasto_id?.toString() || '',
      tipo_gasto_nombre: gasto.tipo_gasto_nombre || '',
      proveedor_id: gasto.proveedor_id?.toString() || '',
      proveedor_nombre: gasto.proveedor_nombre || '',
      cliente_id: gasto.cliente_id?.toString() || '',
      cliente_nombre: gasto.cliente_nombre || '',
      facturador_id: gasto.facturador_id?.toString() || '',
      facturador_nombre: gasto.facturador_nombre || '',
      fecha: gasto.fecha || new Date().toISOString().split('T')[0],
      descripcion: gasto.descripcion || '',
      importe_base: gasto.importe_base || 0,
      tipo_impuesto: gasto.tipo_impuesto || 'IVA 21%',
      porcentaje_iva: gasto.porcentaje_iva || 21,
      porcentaje_retencion: gasto.porcentaje_retencion || 0
    });
    setEditingGasto(gasto);
    setIsDialogOpen(true);
  };

  const handleDelete = async (gasto) => {
    if (!window.confirm(`¿Eliminar el gasto "${gasto.descripcion || 'sin descripción'}"?`)) return;
    
    try {
      await gastosAPI.delete(gasto.id);
      toast({ title: 'Gasto eliminado' });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el gasto',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = {
        ...form,
        tipo_gasto_id: form.tipo_gasto_id ? parseInt(form.tipo_gasto_id) : null,
        proveedor_id: form.proveedor_id ? parseInt(form.proveedor_id) : null,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        facturador_id: form.facturador_id ? parseInt(form.facturador_id) : null,
        importe_base: parseFloat(form.importe_base) || 0,
        porcentaje_iva: parseFloat(form.porcentaje_iva) || 0,
        porcentaje_retencion: parseFloat(form.porcentaje_retencion) || 0
      };
      
      if (editingGasto) {
        await gastosAPI.update(editingGasto.id, data);
        toast({ title: 'Gasto actualizado' });
      } else {
        await gastosAPI.create(data);
        toast({ title: 'Gasto creado' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo guardar el gasto',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTipoGastoChange = (tipoId) => {
    const tipo = tiposGasto.find(t => t.id.toString() === tipoId);
    setForm({
      ...form,
      tipo_gasto_id: tipoId,
      tipo_gasto_nombre: tipo?.nombre || ''
    });
  };

  const handleProveedorChange = (provId) => {
    const prov = proveedores.find(p => p.id.toString() === provId);
    setForm({
      ...form,
      proveedor_id: provId,
      proveedor_nombre: prov?.nombre || ''
    });
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.id.toString() === clienteId);
    setForm({
      ...form,
      cliente_id: clienteId,
      cliente_nombre: cliente?.nombre || ''
    });
  };

  const handleFacturadorChange = (factId) => {
    const fact = facturadores.find(f => f.id.toString() === factId);
    setForm({
      ...form,
      facturador_id: factId,
      facturador_nombre: fact?.nombre || fact?.email || ''
    });
  };

  const handleTipoImpuestoChange = (tipoImpuesto) => {
    const tipo = TIPOS_IMPUESTO.find(t => t.value === tipoImpuesto);
    setForm({
      ...form,
      tipo_impuesto: tipoImpuesto,
      porcentaje_iva: tipo?.iva || 0
    });
  };

  // Calcular totales del formulario
  const calcularTotales = () => {
    const base = parseFloat(form.importe_base) || 0;
    const iva = base * (parseFloat(form.porcentaje_iva) / 100);
    const retencion = base * (parseFloat(form.porcentaje_retencion) / 100);
    const total = base + iva - retencion;
    return { iva, retencion, total };
  };

  const totalesForm = calcularTotales();

  // Exportación
  const handleExport = async (format) => {
    try {
      const params = {};
      if (exportFechaDesde) params.fecha_desde = exportFechaDesde;
      if (exportFechaHasta) params.fecha_hasta = exportFechaHasta;
      
      let response;
      let filename;
      
      if (format === 'csv') {
        response = await gastosAPI.exportCSV(params);
        filename = `gastos_${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        response = await gastosAPI.exportExcel(params);
        filename = `gastos_${new Date().toISOString().split('T')[0]}.xls`;
      }
      
      // Descargar archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: `Exportado a ${format.toUpperCase()}` });
      setIsExportDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo exportar',
        variant: 'destructive'
      });
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading && gastos.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header con icono de enlace */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">📎</span>
        <h1 className="text-2xl font-bold text-blue-700">Controles de gastos</h1>
      </div>
      <p className="text-sm text-gray-600 mb-1">
        Estos son todos los elementos que se han creado en <strong>Quolaw</strong>.
      </p>
      <p className="text-xs text-gray-500 mb-4">
        Pulsa en los títulos de las columnas para ordenar y utiliza el buscador para filtrar.
      </p>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-t-4 border-t-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Controles de gastos</p>
            <p className="text-2xl font-bold text-blue-700">{stats.total_controles}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-gray-400">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Importes</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.total_importes)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-gray-400">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">IVA</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.total_iva)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-gray-400">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">IRPF</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.total_irpf)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_coste)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium mb-2">Controles de gastos</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Busca en la tabla"
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="bg-green-600 text-white hover:bg-green-700"
                onClick={() => setIsExportDialogOpen(true)}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                EXPORTAR
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleOpenNew}
              >
                <Plus className="w-4 h-4 mr-2" />
                NUEVO
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="w-8 px-2 py-3"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Tipo de gasto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Proveedor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Facturador
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100">
                    Fecha ↓
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Descripción
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                    Importes
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {gastos.length > 0 ? (
                  gastos.map((gasto) => (
                    <tr key={gasto.id} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-3">
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {gasto.tipo_gasto_nombre || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-green-600" />
                          <span className="text-blue-600 hover:underline cursor-pointer">
                            {gasto.proveedor_nombre || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {gasto.cliente_nombre ? (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-green-600" />
                            <span>{gasto.cliente_nombre}</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {gasto.facturador_nombre ? (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-green-600" />
                            <span>{gasto.facturador_nombre}</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {formatDate(gasto.fecha)}
                      </td>
                      <td className="px-4 py-3 text-sm max-w-[300px] truncate">
                        {gasto.descripcion || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        {formatCurrency(gasto.coste_total)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button 
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Editar"
                            onClick={() => handleEdit(gasto)}
                          >
                            <Edit className="w-4 h-4 text-gray-600" />
                          </button>
                          <button 
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Eliminar"
                            onClick={() => handleDelete(gasto)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-4 py-12 text-center text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No se encontraron gastos</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Items por página</span>
              <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} de {totalCount}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                ←
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                →
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Nuevo/Editar Gasto */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGasto ? 'Editar control de gastos' : 'Nuevo control de gastos'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Tipo de gasto y Fecha */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de gasto</Label>
                  <Select value={form.tipo_gasto_id} onValueChange={handleTipoGastoChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo de gasto" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposGasto.map(tipo => (
                        <SelectItem key={tipo.id} value={tipo.id.toString()}>
                          {tipo.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input 
                      type="date" 
                      className="pl-10"
                      value={form.fecha}
                      onChange={(e) => setForm({...form, fecha: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Proveedores */}
              <div>
                <Label>Proveedores</Label>
                <Select value={form.proveedor_id} onValueChange={handleProveedorChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Proveedores" />
                  </SelectTrigger>
                  <SelectContent>
                    {proveedores.map(prov => (
                      <SelectItem key={prov.id} value={prov.id.toString()}>
                        {prov.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clientes */}
              <div>
                <Label>Clientes</Label>
                <Select value={form.cliente_id} onValueChange={handleClienteChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map(cliente => (
                      <SelectItem key={cliente.id} value={cliente.id.toString()}>
                        {cliente.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Facturadores */}
              <div>
                <Label>Facturadores</Label>
                <Select value={form.facturador_id} onValueChange={handleFacturadorChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Facturadores" />
                  </SelectTrigger>
                  <SelectContent>
                    {facturadores.map(fact => (
                      <SelectItem key={fact.id} value={fact.id.toString()}>
                        {fact.nombre || fact.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Descripción */}
              <div>
                <Label>Descripción</Label>
                <Input 
                  value={form.descripcion}
                  onChange={(e) => setForm({...form, descripcion: e.target.value})}
                  placeholder="Descripción"
                />
              </div>

              {/* Importes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Importes | Importe</Label>
                  <div className="relative">
                    <Input 
                      type="number"
                      step="0.01"
                      value={form.importe_base}
                      onChange={(e) => setForm({...form, importe_base: e.target.value})}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                  </div>
                </div>
                <div>
                  <Label>Tipo de impuesto</Label>
                  <Select value={form.tipo_impuesto} onValueChange={handleTipoImpuestoChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo de impuesto" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_IMPUESTO.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Retención y Coste total */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Retención</Label>
                  <div className="relative">
                    <Input 
                      type="number"
                      step="0.01"
                      value={form.porcentaje_retencion}
                      onChange={(e) => setForm({...form, porcentaje_retencion: e.target.value})}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                </div>
                <div>
                  <Label>Coste total</Label>
                  <div className="relative">
                    <Input 
                      type="text"
                      value={totalesForm.total.toFixed(2)}
                      readOnly
                      className="pr-8 bg-gray-50 border-dashed"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                  </div>
                </div>
              </div>

              {/* Botón adjuntar */}
              <Button type="button" variant="outline" className="w-full bg-blue-600 text-white hover:bg-blue-700">
                <Upload className="w-4 h-4 mr-2" />
                AÑADIR ADJUNTO
              </Button>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
                CANCELAR
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                GUARDAR
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Exportar */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar Gastos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Selecciona el rango de fechas para exportar los gastos.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha desde</Label>
                <Input 
                  type="date"
                  value={exportFechaDesde}
                  onChange={(e) => setExportFechaDesde(e.target.value)}
                />
              </div>
              <div>
                <Label>Fecha hasta</Label>
                <Input 
                  type="date"
                  value={exportFechaHasta}
                  onChange={(e) => setExportFechaHasta(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Deja vacío para exportar todos los gastos.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleExport('csv')}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <Button 
              onClick={() => handleExport('excel')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar XLS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ControlesGastos;
